import MarkdownIt from 'markdown-it';
import anchor from 'markdown-it-anchor';
import { resolveMdHrefToLogical } from '~/utils/docs-markdown-links';

export interface DocsTocItem {
  level: 2 | 3;
  text: string;
  id: string;
}

interface DocsRenderEnv {
  folderPath?: string;
  helpBase?: string;
}

// html: true is safe only because docs/**/*.md is bundled at build time from this
// repo (see rawLoaders in docs-manifest.ts) — same trust boundary as our .vue files.
// If this ever loads content from a CMS, translation service, or user input, add
// sanitization (e.g. DOMPurify) before rendering with v-html in DocsProse.vue.
const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
}).use(anchor, {
  permalink: false,
});

const defaultFence = md.renderer.rules.fence!;

md.renderer.rules.fence = (tokens, idx, options, env, self) => {
  const token = tokens[idx];
  if (token.info.trim() === 'mermaid') {
    return `<div class="mermaid">${md.utils.escapeHtml(token.content)}</div>\n`;
  }
  return defaultFence(tokens, idx, options, env, self);
};

md.use(mdInstance => {
  const defaultLinkOpen =
    mdInstance.renderer.rules.link_open ??
    ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));

  mdInstance.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    const hrefAttr = tokens[idx].attrIndex('href');
    if (hrefAttr >= 0) {
      const href = tokens[idx].attrs![hrefAttr][1];
      const renderEnv = env as DocsRenderEnv;
      const folderPath = renderEnv.folderPath ?? '';
      const helpBase = renderEnv.helpBase ?? '/help';

      const hashIdx = href.indexOf('#');
      const pathPart = hashIdx >= 0 ? href.slice(0, hashIdx) : href;
      const hash = hashIdx >= 0 ? href.slice(hashIdx) : '';

      if (pathPart.toLowerCase().endsWith('.md')) {
        const logical = resolveMdHrefToLogical(pathPart, folderPath);
        if (logical) {
          const prefix = helpBase.replace(/\/$/, '');
          tokens[idx].attrs![hrefAttr][1] = `${prefix}/${logical}${hash}`;
        }
      }
    }

    return defaultLinkOpen(tokens, idx, options, env, self);
  };
});

export function extractTocFromMarkdown(src: string): DocsTocItem[] {
  const tokens = md.parse(src, {});
  const toc: DocsTocItem[] = [];

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (t.type === 'heading_open') {
      const level = parseInt(t.tag.slice(1), 10);
      if (level === 2 || level === 3) {
        const inline = tokens[i + 1];
        const id = t.attrGet('id') || '';
        const text = inline?.type === 'inline' ? inline.content : '';
        if (id) {
          toc.push({ level: level as 2 | 3, text, id });
        }
      }
    }
  }
  return toc;
}

export function renderDocHtml(
  raw: string,
  folderPath: string,
  options?: { helpBase?: string },
): { html: string; toc: DocsTocItem[] } {
  const helpBase = options?.helpBase ?? '/help';
  const toc = extractTocFromMarkdown(raw);
  const html = md.render(raw, { folderPath, helpBase });
  return { html, toc };
}
